import {Component, Inject, Input, OnDestroy, OnInit, Renderer2, ViewEncapsulation, DOCUMENT, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {StateService} from '../../services/state.service';
import {Subscription} from 'rxjs';
import {CoolLocalStorage} from '@angular-cool/storage';
import {AnalyticsService} from '../../services/analytics.service';

import {InlineWebbookerComponent} from "../../../integrations/inline-webbooker/inline-webbooker.component";
import {TranslateService} from "@ngx-translate/core";
import {DomSanitizer} from "@angular/platform-browser";

import {saveRecentPassenger, RECENT_PASSENGERS_KEY} from '../../services/recent-passengers';

const CUSTOMER_FIELDS = ['firstName', 'lastName', 'email', 'mobilePhone', 'phoneNumber', 'countryCode', 'company', 'notes'];

@Component({
    selector: 'app-confirmation',
    templateUrl: './confirmation.component.html',
    encapsulation: ViewEncapsulation.Emulated,
    styleUrls: ['./confirmation.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ConfirmationComponent implements OnInit, OnDestroy {
  @Input() status: string;
  @Input() navigation: string;
  @Input() formStyle: string;
  @Input() parent: InlineWebbookerComponent;

  loadedScripts: any = {};
  form: any = {config: {parts: []}};
  logo: string;
  statuses = {
    COMPLETED: 'completed',
    FAILED: 'failed',
    BOOKED: 'booked',
  };
  dataSubscription: Subscription;
  retryUrl = '';
  formId = '';

  constructor(private activatedRoute: ActivatedRoute,
              private state: StateService,
              private vault: CoolLocalStorage,
              private analytics: AnalyticsService,
              private route: ActivatedRoute,
              private router: Router,
              private _renderer2: Renderer2,
              private _translateService: TranslateService,
              private domSanitizer:DomSanitizer,
              @Inject('Window') private window: Window,
              @Inject(DOCUMENT) private document: Document) {
    this.status = activatedRoute.snapshot.params.status;
    this.dataSubscription = (this.activatedRoute.parent ? this.activatedRoute.parent : this.activatedRoute).data.subscribe((data: {form: any}) => {
      if(data && data.form) {
        this.logo = data.form.config.logo;
        this.formId = data.form.id;
        this.form = data.form;
      }
    });
  }

  retryPayment(): void {
    const paymentMeta = this.vault.getObject(this.state.storageKeys.paymentMeta);
    if(paymentMeta) {
      this.document.location.href = paymentMeta[`paymentUrl`];
    } else {
      this.bookNewRide();
    }
  }

  ngOnInit(): void {
    // GTM keeps the terminal virtual page view; Umami gets explicit funnel/payment events
    // below (skipUmami) so the routed/redirect flow chains the same names as the inline flow.
    this.analytics.pageView(
      `/${this.formId}/${(this.status === this.statuses.BOOKED ? 'booked' : `payment-${this.status}`)}`,
      undefined, true);
    this.trackTerminalEvents();
    this.rememberPassenger();
  }

  /** Store the passenger of a finished booking so the first-name field can suggest them next time. */
  private rememberPassenger(): void {
    const status = (this.status || '').toLowerCase();
    if (status !== this.statuses.BOOKED && status !== this.statuses.COMPLETED && status !== 'paid') return;
    try {
      saveRecentPassenger(JSON.parse(this.vault.getItem(this.state.storageKeys.values) || '{}'));
    } catch (_) {}
  }

  /**
   * Single source of the terminal Umami funnel/payment events. ConfirmationComponent is
   * the universal end-of-funnel screen — it is mounted BOTH inline as the completion step
   * (order.component.html, with [parent] set) AND as the routed /booked|/completed|/failed
   * page — so firing here (and nowhere else) guarantees each event fires exactly once.
   *  - booked            -> booking_completed (direct booking, no online payment)
   *  - completed | paid  -> booking_completed + payment_completed (only if a payment was started)
   *  - anything else      -> payment_failed (reason = the status / last known PSP status)
   * `status` is the route param when routed, or order.component's `orderStatus` when inline —
   * the latter can be a raw PSP status (e.g. 'paid', 'cancelled'), hence the broad matching.
   */
  private trackTerminalEvents(): void {
    const paymentStarted = !!this.vault.getItem(this.state.storageKeys.paymentStarted);
    const paymentMeta: any = this.vault.getObject(this.state.storageKeys.paymentMeta) || {};
    // Booking amount (major units): prefer the paid amount from the payment metadata;
    // for a direct booking (no online payment) fall back to the inline parent's total.
    const amountCents = (paymentMeta.amount != null)
      ? paymentMeta.amount
      : ((this.parent && this.parent.totalPrice) ? this.parent.totalPrice : undefined);
    const base = {
      implementation: (this.parent && this.parent.getImplementation) ? this.parent.getImplementation() : 'standalone',
      formId: this.formId,
      transactionId: paymentMeta.id || paymentMeta.transactionId,
      gateway: paymentMeta.gateway,
      amount: (amountCents != null) ? amountCents / 100 : undefined,
      currency: paymentMeta.currency || this.state.currency,
    };
    const status = (this.status || '').toLowerCase();
    const booked = status === this.statuses.BOOKED;
    const succeeded = booked || status === this.statuses.COMPLETED || status === 'paid';
    if (!succeeded) {
      // Negative action: the booking/payment did not complete — record why.
      this.analytics.umamiEvent('payment_failed', {
        ...base,
        stage: 'verify',
        reason: status || paymentMeta.status || 'redirect_failed',
      });
      return;
    }
    // Paid flow only: close the payment leg opened by payment_initiated.
    if (paymentStarted && !booked) {
      this.analytics.umamiEvent('payment_completed', { ...base, status: status || 'completed' });
    }
    // Funnel end: the booking is finalised (booked = no payment, completed/paid = paid).
    this.analytics.umamiEvent('booking_completed', { ...base, status: status || 'completed' });
  }

  getInnerHTMLValue(){
    return this.domSanitizer.bypassSecurityTrustHtml(this._translateService.instant(`order_confirmation_text_${this.status}`));
  }

  private saveCustomerData(): void {
    try {
      let stored = JSON.parse(this.vault.getItem(this.state.storageKeys.values) || '{}');
      // OrderComponent.clearState() already wiped the stored values when the booking finished;
      // inline, the parent's live form still holds them.
      if (!Object.keys(stored).length && this.parent?.formGroup) {
        stored = this.parent.formGroup.getRawValue();
      }
      const customerData: any = {};
      CUSTOMER_FIELDS.forEach(field => {
        if (stored[field] !== undefined && stored[field] !== null && stored[field] !== '') {
          customerData[field] = stored[field];
        }
      });
      // Retain address fields so the next booking can pre-fill departure/destination
      (this.form.config.parts || []).forEach((part: any) => {
        (part.fields || []).forEach((field: any) => {
          if (field.type === 'address') {
            if (stored[field.property]) customerData[field.property] = stored[field.property];
            if (stored[field.property + 'Model']) customerData[field.property + 'Model'] = stored[field.property + 'Model'];
          } else if (field.type === 'sortable-address') {
            if (stored.stopOvers?.length) customerData.stopOvers = stored.stopOvers;
            if (stored.stopOversModel?.length) customerData.stopOversModel = stored.stopOversModel;
          }
        });
      });
      if (Object.keys(customerData).length > 0) {
        this.window.sessionStorage.setItem('wb-retain-customer', JSON.stringify(customerData));
      }
    } catch (_) {}
  }

  bookNewRide() {
    // Funnel: the user chose to start a fresh booking after finishing one. Fire before
    // any reset so the (non-PII) context is still intact. Implementation comes from the
    // parent OrderComponent; falls back to 'standalone' when there is no parent.
    this.analytics.umamiEvent('new_booking_started', {
      implementation: (this.parent && this.parent.getImplementation) ? this.parent.getImplementation() : 'standalone',
      formId: this.formId,
      fromStatus: this.status,
    });

    const language = (this.state.language ? this.state.language : this.route.snapshot.paramMap.get('language') ? this.route.snapshot.paramMap.get('language') : this.route.parent.snapshot.paramMap.get('language'));

    this.saveCustomerData();
    this.state.reset();
    // vault.clear() wipes the whole localStorage; the address autocomplete's recent
    // addresses must survive a finished booking, so put them back afterwards.
    let recentAddresses: string | null = null;
    try { recentAddresses = localStorage.getItem('recentAddresses'); } catch { /* storage blocked */ }
    let recentPassengers: string | null = null;
    try { recentPassengers = localStorage.getItem(RECENT_PASSENGERS_KEY); } catch { /* storage blocked */ }
    this.vault.clear();
    try { if (recentPassengers) localStorage.setItem(RECENT_PASSENGERS_KEY, recentPassengers); } catch { /* storage blocked */ }
    try { if (recentAddresses) localStorage.setItem('recentAddresses', recentAddresses); } catch { /* storage blocked */ }

    const navigateToForm = () => {
      if (this.form.config.returnUrl) {
        document.location = this.form.config.returnUrl;
      } else {
        document.location = `/${language}/${(this.form.config.customUrl ? this.form.config.customUrl : this.state.formId)}`;
      }
    };

    if (!this.parent || this.formStyle === 'webbooker') {
      navigateToForm();
    } else if (this.navigation === 'modal') {
      // in-place reload: the component instance is reused, so the previous
      // booking's orderId must be cleared or saveOrder() PATCHes the old order
      // instead of POSTing a new one (no new booking is created/forwarded).
      this.parent.orderId = '';
      this.parent.setup();
      (this.parent as any).closeMyModal();
    } else {
      // inline widget — always reload in-place; savedValues cleared so loadSavedValues()
      // picks up only the retained customer+address data from sessionStorage.
      // Clear orderId too (see modal branch) so a fresh order is created.
      this.parent.orderId = '';
      this.parent.savedValues = {};
      this.parent.selectedFormIndex = 0;
      this.parent.pageStates = [];
      this.parent.loadForm();
    }
  }

  ngOnDestroy(): void {
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }
  }

}
